import React from 'react';
import { Plus } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { CustomizationOptionsEditor } from '../custom/CustomizationOptionsEditor';
import { BundleConfigEditor } from './BundleConfigEditor';

export const MenuItemDialog = ({ 
  open, 
  onOpenChange, 
  editingItem, 
  formData, 
  setFormData, 
  categories, 
  onSubmit, 
  onReset 
}) => {
  const handleOpenChange = (isOpen) => {
    onOpenChange(isOpen);
    if (!isOpen) onReset();
  };

  const selectedCategory = categories.find(c => c.name === formData.category);
  const hasSubcategories = selectedCategory?.subcategories?.length > 0;
  const productType = formData.item_type || 'standard';
  const isBundle = productType === 'bundle';

  // For the "Preview builder" button we synthesise a bundle preview object
  // matching what the customer endpoint would return.
  const previewBundle = isBundle
    ? {
        id: editingItem?.id || 'preview',
        name: formData.name || 'Untitled bundle',
        description: formData.description || '',
        price: Number(formData.price) || 0,
        image_url: formData.image_url,
        category: formData.category,
        available: !!formData.available,
        item_type: 'bundle',
        bundle_config: formData.bundle_config || null,
      }
    : null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button data-testid="add-menu-item-button" className="rounded-full">
          <Plus className="h-4 w-4 mr-2" />
          Add Item
        </Button>
      </DialogTrigger>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>{editingItem ? 'Edit Item' : 'Add New Item'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          {/* Product type selector — controls whether the Bundle editor appears. */}
          <div className="rounded-lg border p-3 bg-secondary/30">
            <Label className="text-sm font-semibold">Product Type</Label>
            <div className="flex gap-3 mt-2">
              {[
                { value: 'standard', label: 'Standard Menu Item' },
                { value: 'bundle', label: 'Meal Deal / Bundle' },
              ].map((opt) => (
                <label
                  key={opt.value}
                  className={`flex-1 border rounded-lg p-2 cursor-pointer transition-colors ${
                    productType === opt.value ? 'border-primary bg-primary/5' : 'border-gray-200'
                  }`}
                >
                  <input
                    type="radio"
                    name="product-type"
                    value={opt.value}
                    checked={productType === opt.value}
                    onChange={() =>
                      setFormData({
                        ...formData,
                        item_type: opt.value,
                        bundle_config:
                          opt.value === 'bundle'
                            ? formData.bundle_config || {
                                feeds_label: '',
                                kitchen_name: '',
                                max_qty_per_order: 10,
                                available_days: [0, 1, 2, 3, 4, 5, 6],
                                order_types: ['collection', 'delivery'],
                                fixed_items: [],
                                groups: [],
                              }
                            : null,
                      })
                    }
                    className="mr-2 accent-primary"
                    data-testid={`product-type-${opt.value}`}
                  />
                  <span className="text-sm font-medium">{opt.label}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <Label htmlFor="name">Name</Label>
            <Input
              data-testid="item-name-input"
              id="name"
              required
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              className="mt-1.5"
            />
          </div>
          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              data-testid="item-description-input"
              id="description"
              required
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              className="mt-1.5"
              rows={3}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="price">Price (£)</Label>
              <Input
                data-testid="item-price-input"
                id="price"
                type="number"
                step="0.01"
                required
                value={formData.price}
                onChange={(e) => setFormData({...formData, price: e.target.value})}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="category">Category</Label>
              <Select
                value={formData.category}
                onValueChange={(value) => setFormData({...formData, category: value, subcategory: ''})}
              >
                <SelectTrigger className="mt-1.5" data-testid="item-category-select">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.name}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {formData.category && hasSubcategories && (
            <div>
              <Label htmlFor="subcategory">Subcategory (Optional)</Label>
              <Select
                value={formData.subcategory || "none"}
                onValueChange={(value) => setFormData({...formData, subcategory: value === "none" ? "" : value})}
              >
                <SelectTrigger className="mt-1.5" data-testid="item-subcategory-select">
                  <SelectValue placeholder="Select subcategory" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {selectedCategory.subcategories.map((sub) => (
                    <SelectItem key={sub.id} value={sub.name}>
                      {sub.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          
          <div className="border-t pt-4">
            {isBundle ? (
              <BundleConfigEditor
                value={formData.bundle_config || null}
                onChange={(cfg) => setFormData({ ...formData, bundle_config: cfg })}
                bundlePreview={previewBundle}
              />
            ) : (
              <CustomizationOptionsEditor
                options={formData.customization_options}
                onChange={(options) => setFormData({ ...formData, customization_options: options })}
              />
            )}
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="available"
              checked={formData.available}
              onChange={(e) => setFormData({...formData, available: e.target.checked})}
              className="w-4 h-4"
            />
            <Label htmlFor="available" className="cursor-pointer">Available</Label>
          </div>
          <Button data-testid="save-item-button" type="submit" className="w-full rounded-full">
            {editingItem ? 'Update Item' : 'Add Item'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
